import { useGetCompanyMediafilesQuery } from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';
import { useEffect, useRef, useState } from 'react';

const PAGE_SIZE = 100;

const useVideoMeiafiles = (companyId: string, query?: string) => {
  const scope = JSON.stringify([companyId, query ?? '']);
  const currentScope = useRef(scope);
  const generation = useRef(0);
  const pending = useRef<{ generation: number } | undefined>(undefined);
  if (currentScope.current !== scope) {
    currentScope.current = scope;
    generation.current += 1;
    pending.current = undefined;
  }
  const [paginationError, setPaginationError] = useState<unknown>();
  useEffect(() => {
    setPaginationError(undefined);
  }, [scope]);
  useEffect(
    () => () => {
      generation.current += 1;
      pending.current = undefined;
    },
    []
  );
  const { data, loading, error, refetch, fetchMore } = useGetCompanyMediafilesQuery({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
    notifyOnNetworkStatusChange: true,
    // A reset must not reuse an in-flight transport from an older generation.
    context: { queryDeduplication: false },
    variables: {
      filters: { companyId, query },
      pagination: { page: 0, pageSize: PAGE_SIZE },
    },
  });

  const getMoreCompanyMediafiles = async () => {
    const pagination = data?.smartlkCompanyMediafilesQuery.paginationInfo;
    if (!pagination?.hasNextPage || loading || pending.current) return;
    const request = { generation: generation.current };
    pending.current = request;
    setPaginationError(undefined);
    try {
      await fetchMore({
        updateQuery: (prev, { fetchMoreResult }) => {
          if (generation.current !== request.generation || pending.current !== request) return prev;
          const previousIds = new Set((prev.smartlkCompanyMediafilesQuery.result ?? []).map((item) => item.id));
          return {
            ...prev,
            smartlkCompanyMediafilesQuery: {
              ...prev?.smartlkCompanyMediafilesQuery,
              ...fetchMoreResult?.smartlkCompanyMediafilesQuery,
              result: [
                ...(prev?.smartlkCompanyMediafilesQuery?.result || []),
                ...(fetchMoreResult.smartlkCompanyMediafilesQuery.result ?? []).filter(
                  (item) => !previousIds.has(item.id)
                ),
              ],
              paginationInfo: {
                ...prev.smartlkCompanyMediafilesQuery.paginationInfo,
                ...fetchMoreResult.smartlkCompanyMediafilesQuery.paginationInfo,
              },
            },
          };
        },
        variables: {
          filters: { companyId, query },
          pagination: { page: pagination.page + 1, pageSize: pagination.pageSize },
        },
      });
    } catch (cause: unknown) {
      if (generation.current === request.generation && pending.current === request) setPaginationError(cause);
    } finally {
      if (pending.current === request) pending.current = undefined;
    }
  };

  const refetchCompanyMediafiles: typeof refetch = async (...args) => {
    generation.current += 1;
    const request = { generation: generation.current };
    pending.current = request;
    setPaginationError(undefined);
    try {
      return await refetch(...args);
    } finally {
      if (pending.current === request) pending.current = undefined;
    }
  };

  return {
    error: error ?? paginationError,
    loading,
    getMoreCompanyMediafiles,
    refetch: refetchCompanyMediafiles,
    companyMediafiles: data?.smartlkCompanyMediafilesQuery.result,
    companyMediafilesPaginationInfo: data?.smartlkCompanyMediafilesQuery.paginationInfo,
  };
};

export default useVideoMeiafiles;
